import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { User, Course, Progress } from '../types';
import authService from '../services/authService';
import { useQuery } from '@tanstack/react-query';

export function useUserProfile(token ?: string, email ?: string) {
  return useQuery({
    queryKey : ['userProfile', email],
    queryFn : async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const userEmail = email || authService.getCurrentUser()?.email || '';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_PROFILE(userEmail)}`;
      
      const response = await fetch(url, {
        method : 'GET',
        headers : {
          'Content-Type' : 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized - redirecting to login');
      }

      const res: any = await response.json();
      
      if (res.error) {
        throw new Error('Failed to fetch user profile');
      }
      console.log('User Profile:', res.data);
      return res.data as any;
    },
    enabled: !!email || !!authService.getCurrentUser()?.email,
  })
};

export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authService.fetchCurrentUser(),
    enabled: authService.isAuthenticated(),
    retry: false,
  });
};

export function useExamAvailability(userId?: string) {
  return useQuery({
    queryKey: ['examAvailability', userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const currentUser = authService.getCurrentUser();
      const currentUserId = userId || currentUser?.id || '7';
      const url = `${baseURL}${API_ENDPOINTS.GET_EXAM_AVAILABILITY(currentUserId)}`;
      
      console.log('Fetching exam availability for user ID:', currentUserId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Raw API Response:', res);
      
      if (res.error) {
        console.error('Exam availability error:', res.error);
        throw new Error('Failed to fetch exam availability');
      }
      
      // Check if response has data field or is direct data
      const examData = res.data || res;
      console.log('Processed Exam Data:', examData);
      return examData;
    },
    enabled: true, // Always enabled since we have fallback user ID
  });
};

export function useUserProgressWithCourseDetails(courseId?: string, userId?: string) {
  return useQuery({
    queryKey: ['userProgressWithCourseDetails', courseId, userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api' ;
      const currentUser = authService.getCurrentUser();
      const currentUserId = userId || currentUser?.id || '7';
      const currentCourseId = courseId || '1';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_PROGRESS_WITH_COURSE_DETAILS(currentCourseId, currentUserId)}`;
      
      console.log('Fetching user progress with course details for course ID:', currentCourseId, 'and user ID:', currentUserId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('Raw User Progress API Response:', res);
      
      if (res.error) {
        console.error('User progress error:', res.error);
        throw new Error('Failed to fetch user progress with course details');
      }
      
      // The API returns data directly based on the image
      const progressData = res.data || res;
      console.log('Processed User Progress Data:', progressData);
      return progressData;
    },
    enabled: !!courseId || !!userId, // Only enabled when we have courseId or userId
  });
};

// Function to update user profile
export const updateUserProfile = async (email: string, profileData: any) => {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  const url = `${baseURL}${API_ENDPOINTS.UPDATE_USER_PROFILE(email)}`;
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authService.getToken()}`,
    },
    body: JSON.stringify(profileData),
  });

  const result = await response.json();
  
  if (!response.ok || result.error) {
    throw new Error(result.message || 'Failed to update profile');
  }
  
  return result.data;
};

// Function to update user password
export const updateUserPassword = async (passwordData: any) => {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  const url = `${baseURL}${API_ENDPOINTS.UPDATE_USER_PASSWORD}`;
  
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authService.getToken()}`,
    },
    body: JSON.stringify(passwordData),
  });

  const result = await response.json();
  
  if (!response.ok || result.error) {
    throw new Error(result.message || 'Failed to update password');
  }
  
  return result.data;
};

// Function to create user progress
export const createUserProgress = async (progressData: {
  user_id: number;
  lesson_id: number; 
  status: number;
  completion_date: string;
}) => {
  const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
  const url = `${baseURL}${API_ENDPOINTS.CREATE_USER_PROGRESS}`;
  
  console.log('Creating user progress with data:', progressData);
  console.log('Full URL:', url);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authService.getToken()}`,
    },
    body: JSON.stringify(progressData),
  });

  const result = await response.json();
  
  console.log('Create user progress response:', result);
  
  if (!response.ok || result.error) {
    throw new Error(result.message || 'Failed to create user progress');
  }
  
  return result.data;
};

// Hook to get user answers dashboard
export function useUserAnswersDashboard(userId?: string) {
  return useQuery({
    queryKey: ['userAnswersDashboard', userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL || 'https://eft-assured-early.ngrok-free.app/api';
      const currentUser = authService.getCurrentUser();
      const currentUserId = userId || currentUser?.id || '1';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_ANSWERS_DASHBOARD(currentUserId)}`;
      
      console.log('Fetching user answers dashboard for user ID:', currentUserId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('User Answers Dashboard Response:', res);
      
      if (res.error) {
        console.error('User answers dashboard error:', res.error);
        throw new Error('Failed to fetch user answers dashboard');
      }
      
      return res.data || res;
    },
    enabled: true,
  });
};

// user service details hook
export function useUserServiceDetails(userId?: string) {
  return useQuery({
    queryKey: ['userServiceDetails', userId],
    queryFn: async () => {
      const baseURL = process.env.REACT_APP_API_URL
        || 'https://eft-assured-early.ngrok-free.app/api';
      const currentUser = authService.getCurrentUser();
      const currentUserId = userId || currentUser?.id || '1';
      const url = `${baseURL}${API_ENDPOINTS.GET_USER_SERVICE_DETAILS(currentUserId)}`;

      console.log('Fetching user service details for user ID:', currentUserId);
      console.log('Full URL:', url);
      
      const res: any = await (await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`,
        },
      })).json();
      
      console.log('User Service Details Response:', res);

      if (res.error) {
        console.error('User service details error:', res.error);
        throw new Error('Failed to fetch user service details');
      }
      
      return res.data || res;
    },
    enabled: true,
  });
};
